import React, { useState, useEffect } from 'react';
import { 
  fetchProviderTemplates, 
  fetchGlobalTemplates,
  triggerNoteGeneration 
} from '../../services/api';

/**
 * NoteGenerationPanel - Configure and trigger note generation
 * Manages all variables for note generation including templates, sections, and transcripts
 */
const NoteGenerationPanel = ({ 
  caseId, 
  authData, 
  providerInfo,
  transcripts = [],
  onNoteGenerated,
  autoGenerate = true,
  isOpen = false,
  onClose
}) => {
  // State for note generation configuration
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  
  // Provider preferences
  const [brevity, setBrevity] = useState('brief'); // brief, standard, detailed
  const [specialty, setSpecialty] = useState(providerInfo?.specialty || '');
  
  // Template selection
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Transcript selection
  const [selectedTranscripts, setSelectedTranscripts] = useState([]);
  
  // Section selection
  const [selectedSections, setSelectedSections] = useState({
    reasonForVisit: true,
    historyOfPresentIllness: true,
    reviewOfSystems: true,
    physicalExam: true,
    assessmentAndPlan: true,
    codingSuggestions: true
  });
  
  // Case info from Core Server
  const [caseInfo, setCaseInfo] = useState(null);
  
  // Auto-generation settings
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(autoGenerate);
  const [lastGeneratedAt, setLastGeneratedAt] = useState(null);

  // Load note templates on mount
  useEffect(() => {
    loadTemplates();
  }, [authData, providerInfo]);

  // Initialize selected transcripts when transcripts prop changes
  useEffect(() => {
    if (transcripts && transcripts.length > 0) {
      // Select all transcripts by default
      setSelectedTranscripts(transcripts.map(t => t.session_id || t.id));
      
    }
  }, [transcripts]);

  // Load case info from localStorage (set by Core Server)
  useEffect(() => {
    const storedCaseInfo = localStorage.getItem('caseInfo');
    if (storedCaseInfo) {
      try {
        setCaseInfo(JSON.parse(storedCaseInfo));
      } catch (e) {
        console.error('Failed to parse case info:', e);
      }
    }
  }, [caseId]);

  const loadTemplates = async () => {
    if (!authData?.api_token) return;
    
    setLoadingTemplates(true);
    try {
      const apiToken = authData.api_token;
      const providerId = providerInfo?.provider_id || providerInfo?.id;
      
      if (!providerId) {
        console.error('Provider ID not available for template loading');
        setTemplates([]);
        return;
      }

      // Load provider templates (includes both provider and health system templates in new API)
      const providerResponse = await fetchProviderTemplates(apiToken, providerId);
      
      // Load global templates separately
      const globalTemplates = await fetchGlobalTemplates(apiToken).catch(() => []);
      
      const allTemplates = [];
      
      // Handle new API structure for provider templates
      if (providerResponse.success) {
        // Add provider templates
        if (providerResponse.provider_templates?.templates) {
          allTemplates.push(...providerResponse.provider_templates.templates.map(t => ({ ...t, source: 'provider' })));
        }
        
        // Add health system templates
        if (providerResponse.health_system_templates?.templates) {
          allTemplates.push(...providerResponse.health_system_templates.templates.map(t => ({ ...t, source: 'health_system' })));
        }
      } else if (Array.isArray(providerResponse)) {
        // Fallback for old API structure
        allTemplates.push(...providerResponse.map(t => ({ ...t, source: 'provider' })));
      }
      
      // Add global templates
      if (Array.isArray(globalTemplates)) {
        allTemplates.push(...globalTemplates.map(t => ({ ...t, source: 'global' })));
      }
      
      setTemplates(allTemplates);
      
      // Auto-select first template if available
      if (allTemplates.length > 0 && !selectedTemplate) {
        setSelectedTemplate(allTemplates[0]);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTranscriptToggle = (transcriptId) => {
    setSelectedTranscripts(prev => {
      if (prev.includes(transcriptId)) {
        return prev.filter(id => id !== transcriptId);
      } else {
        return [...prev, transcriptId];
      }
    });
  };

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleGenerateNote = async () => {
    if (!selectedTemplate) {
      setGenerationError('Please select a note template');
      return;
    }
    
    if (selectedTranscripts.length === 0) {
      setGenerationError('Please select at least one transcript');
      return;
    }

    // Content size validation - estimate transcript size
    const customInstructions = buildCustomInstructions();
    const maxCustomInstructionsLength = 50000; // 50KB limit (matching backend)

    if (customInstructions.length > maxCustomInstructionsLength) {
      setGenerationError(`Custom instructions are too long (${customInstructions.length.toLocaleString()} characters). Maximum allowed: ${maxCustomInstructionsLength.toLocaleString()} characters.`);
      return;
    }

    // Estimate total transcript content size
    let estimatedTranscriptSize = 0;
    selectedTranscripts.forEach(transcriptId => {
      const transcript = transcripts.find(t => t.session_id === transcriptId);
      if (transcript?.session_transcript?.transcript_text) {
        estimatedTranscriptSize += transcript.session_transcript.transcript_text.length;
      }
    });

    const maxTranscriptLength = 5000000; // 5MB limit (matching backend)
    if (estimatedTranscriptSize > maxTranscriptLength) {
      setGenerationError(`Selected transcripts are too large (${estimatedTranscriptSize.toLocaleString()} characters). Maximum allowed: ${maxTranscriptLength.toLocaleString()} characters. Please select fewer or shorter sessions.`);
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    
    try {
      // Prepare generation payload
      const payload = {
        case_id: caseId,
        template_id: selectedTemplate.id,
        transcript_ids: selectedTranscripts,
        provider_preferences: {
          brevity,
          specialty
        },
        sections: selectedSections,
        case_info: caseInfo,
        custom_instructions: buildCustomInstructions()
      };
      
      // Get API token with fallback
      const apiToken = authData?.api_token;
      if (!apiToken) {
        throw new Error('Authentication token not available. Please refresh the page.');
      }
      
      console.log('Generating note with token:', apiToken ? 'Token present' : 'No token');
      console.log('Generation payload:', payload);
      
      // Trigger note generation via API
      const response = await triggerNoteGeneration(
        apiToken,
        payload
      );
      
      if (response.success) {
        setLastGeneratedAt(new Date());
        
        // Send to Core Server via postMessage
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'VISIT_NOTE_GENERATED',
            data: {
              case_id: caseId,
              note_id: response.note_id,
              content: response.content,
              generated_at: new Date().toISOString()
            }
          }, '*');
        }
        
        // Notify parent component
        if (onNoteGenerated) {
          onNoteGenerated(response);
        }
      } else {
        throw new Error(response.error || 'Failed to generate note');
      }
    } catch (error) {
      console.error('Note generation failed:', error);

      // Handle specific HTTP error codes
      if (error.response?.status === 413 || error.message?.includes('413') || error.message?.includes('too large')) {
        setGenerationError('Content is too large to process. Please select fewer sessions or reduce custom instructions length.');
      } else if (error.response?.data?.detail) {
        // Use detailed error message from backend
        setGenerationError(error.response.data.detail);
      } else {
        setGenerationError(error.message || 'Failed to generate note');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const buildCustomInstructions = () => {
    const instructions = [];
    
    // Add brevity instruction
    if (brevity === 'brief') {
      instructions.push('Keep the note concise and focused on key findings.');
    } else if (brevity === 'detailed') {
      instructions.push('Provide comprehensive documentation with detailed observations.');
    }
    
    // Add specialty context
    if (specialty) {
      instructions.push(`Document from the perspective of a ${specialty} specialist.`);
    }
    
    // Add section-specific instructions
    const excludedSections = Object.entries(selectedSections)
      .filter(([_, enabled]) => !enabled)
      .map(([section]) => section);
    
    if (excludedSections.length > 0) {
      instructions.push(`Exclude the following sections: ${excludedSections.join(', ')}`);
    }
    
    // Add case-specific context
    if (caseInfo?.chief_complaint) {
      instructions.push(`Chief complaint: ${caseInfo.chief_complaint}`);
    }
    
    return instructions.join(' ');
  };

  return (
    <div className={`template-editor-panel ${isOpen ? 'open' : ''}`}>
      <div className="scribe-panel-header px-3">
        <h3 className="popup-title">Generate Note</h3>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      {/* Error Display */}
      {generationError && (
        <div className="ankr-auth-status error">
          {generationError}
        </div>
      )}
      <div className="scribe-panel-content" style={{ padding: '16px', overflow: 'auto' }}>
        {/* Auto-generate Switch */}
        <div className="ankr-form-group">
          {/* <label>Auto-Generation</label> */}
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              checked={autoGenerateEnabled}
              onChange={(e) => setAutoGenerateEnabled(e.target.checked)}
              id="autoGenerateSwitch" disabled
            />
            <label className="form-check-label small" htmlFor="autoGenerateSwitch">
              Automatically generate notes when transcripts are available
            </label>
          </div>
        </div>
        {/* Provider Preferences */}
        <div className="ankr-form-group">
          <label>Note Brevity</label>
          <select 
            className="select" 
            value={brevity} 
            onChange={(e) => setBrevity(e.target.value)}
          >
            <option value="brief">Brief</option>
            <option value="standard">Standard</option>
            <option value="detailed">Detailed</option>
          </select>
        </div>

        <div className="ankr-form-group">
          <label>Specialty</label>
          <input
            type="text"
            className="search-input"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            placeholder="e.g., Internal Medicine, Cardiology"
          />
        </div>

        {/* Template Selection */}
        <div className="ankr-form-group">
          <label>Note Template</label>
          {loadingTemplates ? (
            <div className="text-center text-muted">
              Loading templates...
            </div>
          ) : (
            <select 
              className="select"
              value={selectedTemplate?.id || ''} 
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                setSelectedTemplate(template);
              }}
            >
              <option value="">Select a template...</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.source})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Case Info Display */}
        {caseInfo && (
          <div className="ankr-form-group">
            <label>Case Information</label>
            <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
              {caseInfo.patient_name && (
                <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                  <strong>Patient:</strong> {caseInfo.patient_name}
                </div>
              )}
              {caseInfo.chief_complaint && (
                <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                  <strong>Chief Complaint:</strong> {caseInfo.chief_complaint}
                </div>
              )}
              {caseInfo.visit_type && (
                <div style={{ fontSize: '14px' }}>
                  <strong>Visit Type:</strong> {caseInfo.visit_type}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transcript Selection */}
        <div className="ankr-form-group">
          <label>Available Transcripts</label>
          {transcripts.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
              No transcripts available yet
            </div>
          ) : (
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '12px' }}>
              {transcripts.map(transcript => (
                <div key={transcript.session_id || transcript.id} style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={selectedTranscripts.includes(transcript.session_id || transcript.id)}
                      onChange={() => handleTranscriptToggle(transcript.session_id || transcript.id)}
                      style={{ marginRight: '8px' }}
                    />
                    Session {new Date(transcript.created_at).toLocaleString()} 
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      ({transcript.segments?.length || 0} segments
                      {transcript.session_transcript?.transcript_text ? ', transcript available' : ', no transcript'}
                      )
                    </span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section Selection */}
        <div className="ankr-form-group">
          <label>Sections to Generate</label>
          <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedSections.reasonForVisit}
                  onChange={() => handleSectionToggle('reasonForVisit')}
                  style={{ marginRight: '8px' }}
                />
                Reason for Visit
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedSections.physicalExam}
                  onChange={() => handleSectionToggle('physicalExam')}
                  style={{ marginRight: '8px' }}
                />
                Physical Exam
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedSections.historyOfPresentIllness}
                  onChange={() => handleSectionToggle('historyOfPresentIllness')}
                  style={{ marginRight: '8px' }}
                />
                History of Present Illness
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedSections.assessmentAndPlan}
                  onChange={() => handleSectionToggle('assessmentAndPlan')}
                  style={{ marginRight: '8px' }}
                />
                Assessment and Plan
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedSections.reviewOfSystems}
                  onChange={() => handleSectionToggle('reviewOfSystems')}
                  style={{ marginRight: '8px' }}
                />
                Review of Systems
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={selectedSections.codingSuggestions}
                  onChange={() => handleSectionToggle('codingSuggestions')}
                  style={{ marginRight: '8px' }}
                />
                Coding Suggestions (in A&P)
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Generation Actions Status Bar */}
      <div className="scribe-status-bar">
        <div className="status-left">
          <button
            className="button primary mr-2"
            onClick={handleGenerateNote}
            disabled={isGenerating || selectedTranscripts.length === 0 || !selectedTemplate}
          >
            {isGenerating ? (
              'Generating...'
            ) : (
              <>
                <span className="me-1">✨</span>
                Generate Note
              </>
            )}
          </button>
          
          {lastGeneratedAt && (
            <span className="text-muted small">
              Last generated: {lastGeneratedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="status-right"></div>
      </div>
    </div>
  );
};

export default NoteGenerationPanel;